/* EditAuditPolicyComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./EditAuditPolicyRenderer");

const {buildDeviceFilterColumns} = require("../../../../utils/deviceFilterColumns");

class EditAuditPolicyViewComponent extends EventEmitter {
    constructor(context) {
        super();
        this._parentView = _.get(context, "parentView");
        this._currentRow = _.get(context, "currentRow");
        this._logger = _.get(context, "parentView.logger");
        this._deviceEntityDef = _.get(context, "parentView._deviceEntityDef");
        this._CustomFieldsEntityClass = _.get(context, "parentView._customFieldEntityDef");
        this._customFieldsWithInherited = {};
        this._customFieldsEntityInstance = {};
        this._constructTypeEntityDef = _.get(
            this._parentView,
            "_gameEditor._constructTypeEntityDef"
        );
        this._newRule = _.get(context, "newRule");
        // One-shot: asks the fresh subview to open straight into the JSON
        // pane. The renderer consumes it once the form is ready.
        this._startInJsonMode = _.get(context, "startInJsonMode", false);
        this._nodeProps = {};
        this._osMap = undefined;
        this._userDataStorage = this._parentView._userDataStorage;

        this._siteEntityDef = this._parentView._siteEntityDef;

        this._renderer = new Renderer(this);

        this._registerListeners();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get columnConfig() {
        if (this.columns) {
            return this.columns;
        }
        this.columns = buildDeviceFilterColumns({
            vendorsMap: this._parentView._vendorMap,
            accessStatusLabels: this._deviceEntityDef.getAccessStatusLabels(),
            typeBaseGroupMap: this._parentView._typeBaseMap,
            osImageServerList: _.get(this._parentView, "_gameEditor.osImageServerList", []),
            osMap: this._osMap,
            sitePaths: this._sitePaths,
            locationTypeLabels: this._siteEntityDef.getLocationTypeLabels(),
            osUpgradeStatusValues: this._deviceEntityDef.getOsUpgradeStatusValues(),
            tags: this._parentView._tags,
        });
        return this.columns;
    }

    shutdown() {
        this._unregisterListeners();

        return this._renderer
            .shutdown()
            .catch((err) => {
                if (this._logger) {
                    this._logger.warn(err, "EditAuditPolicyViewComponent.shutdown() error.");
                }
            })
            .finally(() => {
                this._renderer = undefined;
                this._parentView = undefined;
                this._currentRow = undefined;
                this._deviceEntityDef = undefined;
                this._CustomFieldsEntityClass = undefined;
                this._customFieldsWithInherited = undefined;
                this._customFieldsEntityInstance = undefined;
                this._constructTypeEntityDef = undefined;
                this._nodeProps = undefined;
                this._osMap = undefined;
                this._userDataStorage = undefined;
                this._siteEntityDef = undefined;
                this._sitePaths = undefined;
                this.columns = undefined;
                this._logger = undefined;
            });
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    async _getSitePaths() {
        this._sitePaths = await this._parentView._siteEntityDef.loadSites(
            this._parentView._userSession.activeOrganization.id,
            {},
            {
                id: true,
                path: true,
                name: true,
                locationType: true,
            },
            {
                path: 1,
            }
        );
    }

    async _getCommandSets(osName) {
        let query = {enabled: true};
        const activeOrgId = this._parentView._userSession.activeOrganization.id;
        if (osName !== "all") {
            query = {$and: [query, {os: osName}]};
        }
        if (_.isEmpty(this._parentView._commandSetFeed)) {
            this._parentView._commandSetFeed =
                this._parentView._commandSetEntityDef.startCommandSetsFeed(
                    activeOrgId,
                    query,
                    {
                        name: true,
                        id: true,
                        os: true,
                        dataType: true,
                        commands: true,
                        conceptItemIds: true,
                        rsiIds: true,
                        getUrls: true,
                        basicMode: true,
                    },
                    {},
                    {skip: 0, limit: 200}
                );
        } else {
            this._parentView._commandSetFeed.reestablishFeed(
                activeOrgId,
                query,
                {
                    name: true,
                    id: true,
                    os: true,
                    dataType: true,
                    commands: true,
                    conceptItemIds: true,
                    rsiIds: true,
                    getUrls: true,
                    basicMode: true,
                },
                {}
            );
        }
        return this._parentView._commandSetFeed.whenInitialized();
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    whenModelIsReady() {
        return Promise.resolve()
            .then(() => {
                return this._deviceEntityDef
                    .loadAndMergeOsMap(this._parentView._gameEditor._activeOrgId)
                    .then((val) => {
                        this._osMap = val;
                        return Promise.resolve();
                    });
            })
            .then(() => this._getSitePaths())
            .then(() => this.retrieveCustomFieldsEntity(this._parentView._gameEditor._activeOrgId))
            .then(() => this._getNodeProps())
            .then(() => this._appendColumnConfigWithNodeProps());
    }

    setCustomFieldsEntity(newOrgInstance) {
        this._customFieldsEntityInstance = newOrgInstance;
        return this._customFieldsEntityInstance;
    }

    retrieveCustomFieldsEntity(tenantId) {
        if (this._customFieldsEntityInstance && this._customFieldsEntityInstance.shutdown) {
            this._customFieldsEntityInstance
                .shutdown()
                .catch((error) => this._logger.warn({error}));
        }
        return this._CustomFieldsEntityClass
            .retrieveCustomFields(tenantId)
            .then((customFieldsEntity) => {
                this.setCustomFieldsEntity(customFieldsEntity);
                return customFieldsEntity.loadInheritedFields();
            })
            .then((inheritedFields) => {
                Object.assign(
                    this._customFieldsWithInherited,
                    this._customFieldsEntityInstance.fields,
                    inheritedFields
                );
                return this._customFieldsWithInherited;
            });
    }

    async _getNodeProps() {
        this._nodeProps = await this._constructTypeEntityDef.getDeviceNodePropDefs(
            this._parentView._gameEditor._activeOrgId
        );
        return Promise.resolve();
    }

    async _appendColumnConfigWithNodeProps() {
        let columnConfigMaxPriority = Math.max(..._.map(this.columnConfig, "priority"));
        // Get all existing labels from columnConfig
        const existingLabels = new Set(
            Object.values(this.columnConfig).map((config) => config.label)
        );

        let enabledNodeProps = Object.entries(this._nodeProps)
            .map(([id, props]) => [id.replace(/[\t\n\r ]+/g, " "), props])
            .filter(([normalizedId]) => {
                // Filter out if this label already exists in columnConfig -- If duplicated label, device entity property takes precedence
                return !existingLabels.has(normalizedId);
            })
            .map(([id, props]) => {
                columnConfigMaxPriority = columnConfigMaxPriority + 1;
                let prop = Object.assign({}, props, {priority: columnConfigMaxPriority});
                Object.assign(prop, {
                    id: `nodeProps.${id}`,
                    label: id,
                    path: `nodeProps.${id}`,
                    dataType: props.type,
                });
                if (props.type === "enum") {
                    prop.options = {};
                    props.values.forEach((val) =>
                        Object.assign(prop.options, {[val]: {name: val, title: val}})
                    );
                }
                return {
                    [`nodeProps.${id}`]: prop,
                };
            });
        _.assign(this.columnConfig, ...enabledNodeProps);

        return Promise.resolve();
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = EditAuditPolicyViewComponent;
